import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import { styled } from "@mui/material/styles";
import { CSSProperties } from "@mui/material/styles/createMixins";
import { v4 as uuid } from "uuid";

import {
  MAIN_LOCATIONS_DROPDOWN_STORAGE_KEY,
  getLocationFromLocalStorageByMainStorageKey,
  setLocationToLocalStorage,
} from "../../../helpers/getLocationFromLocalStorage";
import { AutocompleteOptionType } from "../../../models/autocomplete";
import { LeftMenuItems, RouteEnum } from "../../../models/enums";
import {
  selectedLocationDropdownVariable,
  selectedTagIdDropdownVariable,
  selectedTagLocationsDropdownVariable,
} from "../../../variables/selectedLocationDropdown";
import AIManagerMenuIcon from "../../icons/AIManagerMenuIcon";
import AIManagerMenuIconFilled from "../../icons/AIManagerMenuIconFilled";
import CustomerSettingsMenuIcon from "../../icons/CustomerSettingsMenuIcon";
import CustomerSettingsMenuIconFilled from "../../icons/CustomerSettingsMenuIconFilled";
import DataHubMenuIcon from "../../icons/DataHubMenuIcon";
import DataHubMenuIconFilled from "../../icons/DataHubMenuIconFilled";
import DeploymentHealthMenuIcon from "../../icons/DeploymentHealthMenuIcon";
import DeploymentHealthMenuIconFilled from "../../icons/DeploymentHealthMenuIconFilled";
import DeviceMenuIcon from "../../icons/DeviceMenuIcon";
import DeviceMenuIconFilled from "../../icons/DeviceMenuIconFilled";
import GateGuardMenuIcon from "../../icons/GateGuardMenuIcon";
import GateGuardMenuIconFilled from "../../icons/GateGuardMenuIconFilled";
import LiveViewMenuIcon from "../../icons/LiveViewMenuIcon";
import LiveViewMenuIconFilled from "../../icons/LiveViewMenuIconFilled";
import NodeMenuIcon from "../../icons/NodeMenuIcon";
import NodeMenuIconFilled from "../../icons/NodeMenuIconFilled";
import SafetyMenuIcon from "../../icons/SafetyMenuIcon";
import SafetyMenuIconFilled from "../../icons/SafetyMenuIconFilled";
import StatisticsMenuIcon from "../../icons/StatisticsMenuIcon";
import StatisticsMenuIconFilled from "../../icons/StatisticsMenuIconFilled";
import SystemSettingsMenuIcon from "../../icons/SystemSettingsMenuIcon";
import SystemSettingsMenuIconFilled from "../../icons/SystemSettingsMenuIconFilled";
import TankLevelMenuIcon from "../../icons/TankLevelMenuIcon";
import TankLevelMenuIconFilled from "../../icons/TankLevelMenuIconFilled";
import TelemetryMenuIcon from "../../icons/TelemetryMenuIcon";
import TelemetryMenuIconFilled from "../../icons/TelemetryMenuIconFilled";
import AllLocationsFilterDropdownContainer from "../../locations/AllLocationsFilterDropdownContainer";
import MenuItem from "../MenuItem";
import { LeftSideBarSettingsType } from "../UnifiedLayout";

export const listItemButtonCssNoAddIcon = {
  "&.MuiListItemButton-root": {
    paddingBottom: 0,
    paddingTop: 0,
    marginTop: "4px",
    marginBottom: "4px",
  },
};

export const listItemTextCss = {
  fontWeight: 500,
};

export const ListStyled = styled(List)(
  (): CSSProperties => ({
    padding: "1.5em 0.75em",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  })
);

export const ListItemStyled = styled(ListItem)(
  ({ theme }): CSSProperties => ({
    "&:hover": {
      backgroundColor: theme.palette.otherBackdropOverlay,
    },
  })
);
interface IProps {
  open: boolean;
}

export const ListItemButtonStyled = styled(ListItemButton, {
  shouldForwardProp: (propName: string): boolean => propName !== "open",
})<IProps>(
  ({ theme, open }): CSSProperties => ({
    height: "3em",
    padding: "0.5em",
    borderRadius: "33px",
    "&.MuiButtonBase-root.MuiListItemButton-root.MuiListItemButton-root": {
      paddingBottom: 0,
      paddingTop: 0,
      marginTop: 0,
      marginBottom: "4px",
    },
    "&:hover, &.Mui-selected": {
      borderRadius: "33px",
      padding: "0.5em",
      height: "3em",
      backgroundColor: open
        ? theme.palette.otherBackdropOverlay.main
        : "transparent",
      "& .defaultMenuIcon": {
        display: "none",
      },
      "& .filledMenuIcon": {
        display: "inline-flex",
      },
    },
    "&.Mui-selected": {
      backgroundColor: open
        ? theme.palette.otherBackdropOverlay.main
        : "transparent",
      "&:hover": {
        backgroundColor: open
          ? theme.palette.otherBackdropOverlay.main
          : "transparent",
      },
    },
  })
);

export const ListItemIconStyled = styled(ListItemIcon)(
  (): CSSProperties => ({
    minWidth: "32px",
    maxWidth: "32px",
    marginRight: "0.5em",
  })
);

export interface LeftSideBarBaseProps {
  open: boolean;
  settings: LeftSideBarSettingsType;
}

const LeftSideBarBase = ({
  open,
  settings,
}: LeftSideBarBaseProps): JSX.Element => {
  const navigate = useNavigate();
  const pathname = window.location.pathname;

  const menuItems = useMemo(
    () => [
      {
        name: LeftMenuItems.DEVICES,
        icon: <DeviceMenuIcon />,
        iconFilled: <DeviceMenuIconFilled />,
      },
      {
        name: LeftMenuItems.AI_MANAGER,
        icon: <AIManagerMenuIcon />,
        iconFilled: <AIManagerMenuIconFilled />,
      },
      {
        name: LeftMenuItems.NODES,
        icon: <NodeMenuIcon />,
        iconFilled: <NodeMenuIconFilled />,
      },
      {
        name: LeftMenuItems.TELEMETRY,
        icon: <TelemetryMenuIcon />,
        iconFilled: <TelemetryMenuIconFilled />,
        nestedItems: [
          {
            name: LeftMenuItems.TANK_LEVEL,
            icon: <TankLevelMenuIcon />,
            iconFilled: <TankLevelMenuIconFilled />,
          },
          {
            name: LeftMenuItems.GATE_GUARD,
            icon: <GateGuardMenuIcon />,
            iconFilled: <GateGuardMenuIconFilled />,
          },
          // FEATURE FLAG - PERSONNEL DETECTION. PREVENT NEW FEATURE FROM BEING ENABLED.
          // {
          //   name: LeftMenuItems.PEOPLE_AND_VEHICLE,
          //   icon: <SecurityMenuIcon />,
          //   iconFilled: <SecurityMenuIconFilled />,
          // },
          {
            name: LeftMenuItems.HARD_HAT,
            icon: <SafetyMenuIcon />,
            iconFilled: <SafetyMenuIconFilled />,
          },
        ],
      },
      {
        name: LeftMenuItems.CUSTOMER_SETTINGS,
        icon: <CustomerSettingsMenuIcon />,
        iconFilled: <CustomerSettingsMenuIconFilled />,
      },
      {
        name: LeftMenuItems.CONTROL_ROOM_BETA,
        icon: <DataHubMenuIcon />,
        iconFilled: <DataHubMenuIconFilled />,
      },
      {
        name: LeftMenuItems.RECENT_ACTIVITIES,
        icon: <DataHubMenuIcon />,
        iconFilled: <DataHubMenuIconFilled />,
      },

      {
        name: LeftMenuItems.LIVE_VIEW,
        icon: <LiveViewMenuIcon />,
        iconFilled: <LiveViewMenuIconFilled />,
      },
      {
        name: LeftMenuItems.DEPLOYMENT_HEALTH,
        icon: <DeploymentHealthMenuIcon />,
        iconFilled: <DeploymentHealthMenuIconFilled />,
      },
      {
        name: LeftMenuItems.STATISTICS,
        icon: <StatisticsMenuIcon />,
        iconFilled: <StatisticsMenuIconFilled />,
      },
      {
        name: LeftMenuItems.SYSTEM_SETTINGS,
        icon: <SystemSettingsMenuIcon />,
        iconFilled: <SystemSettingsMenuIconFilled />,
        showDivider: true,
      },
    ],
    []
  );

  const hideLocationDropdownCondition =
    pathname.includes(RouteEnum.SystemSettings) ||
    pathname.includes(RouteEnum.RecentActivities) ||
    pathname.includes(RouteEnum.ControlRoom) ||
    pathname.includes(RouteEnum.DeploymentHealth) ||
    pathname.includes(RouteEnum.Telemetry) ||
    pathname.includes(RouteEnum.CustomerSettings) ||
    pathname.includes(RouteEnum.Statistics) ||
    pathname.startsWith("/manage-locations") ||
    pathname.includes("integrations") ||
    pathname.includes("/device/");

  const handleLocationChange = (
    locationValue: AutocompleteOptionType
  ): void => {
    if (!locationValue) {
      selectedLocationDropdownVariable(null);

      const stored = getLocationFromLocalStorageByMainStorageKey();
      const looksLikeTag =
        stored?.title?.startsWith("#") || stored?.value?.includes("#T#");

      if (!looksLikeTag) {
        setLocationToLocalStorage(MAIN_LOCATIONS_DROPDOWN_STORAGE_KEY, null);
      }

      return;
    }

    if (locationValue && !locationValue.value) {
      const currentLocation = selectedLocationDropdownVariable();

      if (currentLocation?.value) {
        return;
      }

      selectedLocationDropdownVariable(null);

      return;
    }

    selectedLocationDropdownVariable(locationValue);

    if (locationValue?.value) {
      selectedTagLocationsDropdownVariable(null);

      selectedTagIdDropdownVariable(null);
    }

    if (locationValue?.value) {
      setLocationToLocalStorage(
        MAIN_LOCATIONS_DROPDOWN_STORAGE_KEY,
        locationValue
      );
    } else {
      setLocationToLocalStorage(MAIN_LOCATIONS_DROPDOWN_STORAGE_KEY, null);
    }
  };

  const handleTagLocationsChange = (
    tagLocations: AutocompleteOptionType[] | null
  ): void => {
    selectedTagLocationsDropdownVariable(tagLocations);

    selectedTagIdDropdownVariable(null);
  };

  const handleTagIdChange = (tagId: string): void => {
    selectedTagIdDropdownVariable(tagId);
  };

  const locationsDropdownDisplayValue = hideLocationDropdownCondition
    ? "none"
    : "block";

  return (
    <>
      {settings.showAllLocationsDropdown && (
        <>
          <Box
            sx={{
              margin: "10px auto",
              minWidth: "220px",
              display: locationsDropdownDisplayValue,
            }}
          >
            <AllLocationsFilterDropdownContainer
              disableClearable={settings.disableClearableInAllLocationsDropdown}
              hideTags={settings.hideTagsInAllLocationsDropdown}
              rememberEmptyValue={
                settings.rememberEmptyValueInAllLocationsDropdown
              }
              removeBorder
              enhancedCapabilities
              menuOpened={open}
              setLocation={handleLocationChange}
              setTagLocations={handleTagLocationsChange}
              setTagId={handleTagIdChange}
            />
          </Box>
          <Divider flexItem sx={{ display: locationsDropdownDisplayValue }} />
        </>
      )}

      <ListStyled>
        {menuItems.map(item => {
          const roleBasedItem = settings.leftSideBarItems.find(
            roleItem => roleItem.name === item.name
          );

          const isPathCommon = pathname === RouteEnum.LiveView;

          const isRecentActivitiesNew =
            pathname.includes(RouteEnum.ControlRoom) ||
            pathname.includes(RouteEnum.SensoryControlRoom);

          let isMenuSelected =
            roleBasedItem && pathname.startsWith(roleBasedItem.path);

          if (roleBasedItem && isPathCommon) {
            isMenuSelected = pathname === roleBasedItem.path;
          }

          if (roleBasedItem && isRecentActivitiesNew) {
            isMenuSelected = pathname === roleBasedItem.path;
          }

          const uniqueId = uuid();

          const [isNestedItemsExpanded, setIsNestedItemsExpanded] =
            useState(false);

          const handleExpandClick = () => {
            setIsNestedItemsExpanded(prev => !prev);
          };

          const handleMenuItemClick = () => {
            if (roleBasedItem?.nonClickable) return;

            setIsNestedItemsExpanded(false);

            if (roleBasedItem?.path) {
              navigate(roleBasedItem.path);
            }
          };

          const hasNestedItems =
            !!item.nestedItems && item.nestedItems.length > 0;

          const iTelemetrysMenuSelected =
            item.name === "Telemetry" &&
            pathname.includes(RouteEnum.Telemetry) &&
            !isNestedItemsExpanded;

          return (
            <React.Fragment key={uniqueId}>
              {roleBasedItem && (
                <>
                  {roleBasedItem.showDivider && (
                    <Divider
                      flexItem
                      component="li"
                      sx={{
                        marginBottom: "4px",
                      }}
                    />
                  )}

                  <MenuItem
                    key={item.name}
                    onClick={handleMenuItemClick}
                    label={item.name}
                    icon={item.icon}
                    iconFilled={item.iconFilled}
                    selected={!!isMenuSelected || iTelemetrysMenuSelected}
                    open={open}
                    hasExpand={hasNestedItems}
                    isExpanded={isNestedItemsExpanded}
                    handleExpandClick={handleExpandClick}
                    listItemStyledProps={{
                      sx: {
                        marginTop: roleBasedItem.isSeparatorOfBottomSection
                          ? "auto"
                          : 0,
                      },
                    }}
                  />

                  {hasNestedItems && (
                    <Collapse
                      in={isNestedItemsExpanded}
                      timeout="auto"
                      unmountOnExit
                      collapsedSize="auto"
                    >
                      <List component="div" disablePadding>
                        {item.nestedItems.map(nestedItem => {
                          const roleBasedNestedItem =
                            roleBasedItem.nestedItems?.find(
                              roleItem => roleItem.name === nestedItem.name
                            );

                          const isNestedMenuItemSelected =
                            roleBasedNestedItem &&
                            pathname.startsWith(roleBasedNestedItem.path);

                          const handleMenuItemClick = () => {
                            roleBasedNestedItem?.path &&
                              navigate(roleBasedNestedItem.path);
                          };

                          return (
                            <MenuItem
                              key={nestedItem.name}
                              onClick={handleMenuItemClick}
                              label={nestedItem.name}
                              icon={nestedItem.icon}
                              iconFilled={nestedItem.iconFilled}
                              selected={!!isNestedMenuItemSelected}
                              open={open}
                              listItemStyledProps={{
                                sx: {
                                  pl: open ? 4 : 0,
                                  marginTop:
                                    roleBasedNestedItem?.isSeparatorOfBottomSection
                                      ? "auto"
                                      : 0,
                                },
                              }}
                            />
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </>
              )}
            </React.Fragment>
          );
        })}
      </ListStyled>
    </>
  );
};

export default LeftSideBarBase;
